import {
  Avatar,
  Box,
  Button,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import config from "../../config";
import { Formik } from "formik";
import * as yup from "yup";
import { Redirect } from "react-router-dom";
import { AuthService, CollectionService } from "../../services";
import { useSnackbar } from "notistack";
import Helmet from "react-helmet";

interface ILoginForm {
  username: string;
  apiKey: string;
}

const initialValue: ILoginForm = {
  username: "",
  apiKey: "",
};

const validationSchema = yup.object().shape<ILoginForm>({
  username: yup.string().required("Username is required"),
  apiKey: yup.string().required("Api key is required"),
});

const AuthModule: React.FC = () => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const notification = useSnackbar();

  useEffect(() => {
    if (AuthService.isAuthenticated()) {
      setIsAuthenticated(true);
    }
  }, []);

  if (isAuthenticated) {
    return <Redirect to="/" />;
  }

  return (
    <Box
      bgcolor={theme.palette.grey[100]}
      display="flex"
      flexGrow={1}
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      p={2}
    >
      <Helmet title="Login | Vector A.I. Playground" />
      <Avatar
        src={config.VECTOR_AI_LOGO}
        alt="logo"
        variant="square"
        style={{ height: theme.spacing(10), width: theme.spacing(10) }}
      />

      <Typography
        variant={isLargeScreen ? "h4" : "h5"}
        style={{
          marginTop: theme.spacing(2),
          fontWeight: theme.typography.fontWeightBold,
        }}
        align="center"
      >
        Sign in to your account
      </Typography>

      <Formik
        validationSchema={validationSchema}
        initialValues={{ ...initialValue }}
        onSubmit={async (values: ILoginForm) => {
          const response = await CollectionService.listCollections(values);
          if (response) {
            setIsAuthenticated(true);
          } else {
            notification.enqueueSnackbar("Invalid username or api key", {
              variant: "error",
            });
          }
        }}
      >
        {({
          handleSubmit,
          values,
          handleChange,
          handleBlur,
          touched,
          errors,
          submitCount,
          isValid,
          isSubmitting,
        }) => (
          <form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Box
              bgcolor={theme.palette.common.white}
              p={4}
              boxShadow="0 1px 3px 0 rgba(0,0,0,.1), 0 1px 2px 0 rgba(0,0,0,.06)"
              my={2}
              borderRadius={4}
              display="flex"
              flexDirection="column"
              minWidth={isLargeScreen ? 400 : "80%"}
            >
              <Box display="flex" flexDirection="column" flexGrow={1} mb={2}>
                <Typography style={{ color: "rgba(114,130,151,0.8)" }}>
                  Username
                </Typography>
                <TextField
                  placeholder="Username"
                  variant="outlined"
                  fullWidth
                  margin="dense"
                  name="username"
                  value={values.username}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={touched.username && errors.username}
                  error={touched.username && Boolean(errors.username)}
                />
              </Box>

              <Box display="flex" flexDirection="column" flexGrow={1} mb={2}>
                <Typography style={{ color: "rgba(114,130,151,0.8)" }}>
                  Api key
                </Typography>
                <TextField
                  type="password"
                  placeholder="Api key"
                  variant="outlined"
                  fullWidth
                  margin="dense"
                  name="apiKey"
                  value={values.apiKey}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={touched.apiKey && errors.apiKey}
                  error={touched.apiKey && Boolean(errors.apiKey)}
                />
              </Box>
              <Button
                color="primary"
                variant="contained"
                type="submit"
                size="large"
                fullWidth
                style={{
                  textTransform: "none",
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: ".5em",
                  color: theme.palette.common.white,
                }}
                disableRipple
                disableElevation
                disabled={(submitCount && !isValid) || isSubmitting}
              >
                {isSubmitting ? "Please wait..." : "Sign in"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default AuthModule;
